import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  FC,
} from 'react';
import updateFilterLegStates from '../../Filters/state/updateFilterLegStates';
import { usePerformedSearchRequest } from 'clientState/providers/SearchRequestProvider';
import { useOrderFilterTagsActions } from 'clientState/providers/OrderedFilterTagsProvider';
import { useConnectionDurationRanges } from 'clientState/providers/ParsedSearchResponseProvider';
import {
  StopDurationFilterContextType,
  StopDurationFilterState,
  StopDurationFilterAction,
  UseStopDurationLegFilterReturn,
  MaxDuration,
  UseStopDurationFilterReturn,
  StopDurationFilterSummary,
  StopDurationFilterProviderProps,
} from './types';

const ALIAS = 'stopDuration';
const StopDurationFilterContext = createContext<StopDurationFilterContextType>([
  [],
  () => {},
]);

const applyLegFilterReducer = (maxDurations, { legIds, maxDuration }) => {
  const [legId] = legIds;
  const currentMaxDuration = maxDurations[legId] || {};
  const nextMaxDuration = maxDuration || currentMaxDuration;
  return updateFilterLegStates(legIds, nextMaxDuration, maxDurations);
};

const stopDurationsReducer = (
  maxDurations: StopDurationFilterState,
  action: StopDurationFilterAction
): StopDurationFilterState => {
  switch (action.type) {
    case 'RESET':
      return [];
    case 'FILTER':
      return applyLegFilterReducer(maxDurations, action);
    default:
      return maxDurations;
  }
};

const isRoundTrip = (searchRequest) =>
  searchRequest && searchRequest.tripType === 'ROUND_TRIP';

export const useStopDurationLegFilter = (
  legId: number
): UseStopDurationLegFilterReturn => {
  const searchRequest = usePerformedSearchRequest();
  const actions = useOrderFilterTagsActions();
  const availableDurations = useConnectionDurationRanges();
  const [maxDurations, dispatch] = useContext(StopDurationFilterContext);
  const defaultMaxDuration = (availableDurations || [])[legId].max;
  const maxDuration = (maxDurations || [])[legId] || defaultMaxDuration;

  const applyLegFilter = useMemo(() => {
    const { add, remove, clearAllStartsWith } = actions;
    const legIds = isRoundTrip(searchRequest) ? [0, 1] : [legId];
    return (maxDuration: MaxDuration) => {
      dispatch({
        type: 'FILTER',
        legIds,
        maxDuration,
      });
      const alias = ALIAS;
      if (maxDuration !== defaultMaxDuration) {
        add({
          alias,
          legId,
          undo: () => {
            dispatch({ type: 'RESET' });
            clearAllStartsWith(ALIAS);
          },
        });
      } else {
        remove({ alias, legId });
      }
    };
  }, [dispatch, legId, searchRequest, actions, defaultMaxDuration]);

  return [maxDuration, applyLegFilter];
};

export const useAppliedStopDurationFilter = (): boolean => {
  const availableDurations = useConnectionDurationRanges();
  const [maxDurations] = useContext(StopDurationFilterContext);
  return (
    maxDurations?.length > 0 &&
    maxDurations?.some(
      (maxDuration, index) =>
        availableDurations?.[index] &&
        maxDuration < availableDurations[index].max
    )
  );
};

export const useStopDurationFilter = (): UseStopDurationFilterReturn => {
  const { clearAllStartsWith } = useOrderFilterTagsActions();
  const [maxDurations, dispatch] = useContext(StopDurationFilterContext);
  const resetFilter = useCallback(() => {
    dispatch({ type: 'RESET' });
    clearAllStartsWith(ALIAS);
  }, [dispatch, clearAllStartsWith]);
  return [maxDurations, resetFilter];
};

export const useStopDurationFilterSummary = (): StopDurationFilterSummary => {
  const availableDurations = useConnectionDurationRanges();
  const [maxDurations] = useContext(StopDurationFilterContext);
  return maxDurations?.map((maxDuration, index) => {
    const { max } = availableDurations?.[index] || {};
    return {
      isApplied: !!(max && maxDuration && maxDuration < max),
      selected: maxDuration,
    };
  });
};

const StopDurationFilterProvider: FC<StopDurationFilterProviderProps> = ({
  children,
}) => {
  const stateAndDispatch = useReducer(stopDurationsReducer, []);

  return (
    <StopDurationFilterContext.Provider value={stateAndDispatch}>
      {children}
    </StopDurationFilterContext.Provider>
  );
};

export default StopDurationFilterProvider;
